import React, { Component } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography, Paper } from "@mui/material";

/**
 * ErrorBoundary Component - Catches JavaScript errors in child component tree
 *
 * This component implements React's Error Boundary pattern to:
 * - Catch errors during rendering, in lifecycle methods, and constructors
 * - Display a fallback UI instead of crashing the entire app
 * - Log error details for debugging
 * - Provide a way to recover (reload the page)
 *
 * Note: Error boundaries do NOT catch errors in:
 * - Event handlers (use try/catch instead)
 * - Asynchronous code (setTimeout, promises)
 * - Server-side rendering
 * - Errors thrown in the error boundary itself
 *
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught
   * This enables the component to render fallback UI on the next render
   */
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  /**
   * Log error details when an error is caught
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Object with componentStack property
   */
  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Store error details in state for display
    this.setState({
      error,
      errorInfo,
    });

    // Here you could also log to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  /**
   * Handle reload button click
   * Reloads the current page to attempt recovery
   */
  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "var(--app-vh)",
            backgroundColor: "#f5f5f5",
            padding: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 600,
              padding: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom color="error">
              Something went wrong
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                color: "text.secondary",
              }}
            >
              We&apos;re sorry, but something unexpected happened. The error has
              been logged and we&apos;ll look into it.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReload}
              sx={{ marginTop: 2 }}
            >
              Reload Page
            </Button>

            {/* Show error details in development mode */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box
                sx={{
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: "#ffebee",
                  borderRadius: 1,
                  textAlign: "left",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Error Details (Development Only):
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontSize: "0.75rem",
                    overflow: "auto",
                    maxHeight: 200,
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
